import posthog from 'posthog-js';

const AMPLIFY_LOCAL_STORAGE_PREFIX = 'CognitoIdentityServiceProvider.';

export const AUTH_CLIENT_FAILURE_EVENT = 'auth_client_failure';
export const AUTH_CLIENT_LOG_PATH = '/auth/client-log';
export const ASSEMBLY_LOGIN_RELOAD_COUNT_KEY = 'assemblyLoginReloadCount';

export const AUTH_CLIENT_LOG_STEPS = [
  'complete_google_login',
  'load_workspaces',
  'workspace_select',
  'current_authenticated_user',
  'sign_in_failure',
  'hosted_ui_failure',
  'invite_email_mismatch',
  'recovery',
] as const;

export type AuthClientLogStep = (typeof AUTH_CLIENT_LOG_STEPS)[number];

export type AuthClientErrorKind =
  | 'http'
  | 'network'
  | 'cors'
  | 'timeout'
  | 'thrown';

export type AuthClientLogPayload = {
  loginAttemptId: string;
  step: AuthClientLogStep;
  outcome: 'failure' | 'recovered';
  recoveryStage?: string;
  errName?: string;
  httpStatus?: number;
  errorKind?: AuthClientErrorKind;
  email?: string;
  portalId?: string;
  hasPortalSessionsCookie?: boolean;
  lastAuthUser?: boolean;
  amplifyKeysPresent?: string[];
  reloadCount?: number;
};

const sentBeacons = new Set<string>();
let assemblyLoginAttemptId = '';

const createLoginAttemptId = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `assembly-${Date.now()}`;
};

export const resetLoginAttemptId = () => {
  assemblyLoginAttemptId = createLoginAttemptId();
  return assemblyLoginAttemptId;
};

export const getLoginAttemptId = () => {
  if (!assemblyLoginAttemptId) {
    assemblyLoginAttemptId = createLoginAttemptId();
  }
  return assemblyLoginAttemptId;
};

export const resetAuthClientLogDedupe = () => {
  sentBeacons.clear();
};

export const classifyAuthError = (
  error: unknown,
): Pick<AuthClientLogPayload, 'errName' | 'httpStatus' | 'errorKind'> => {
  if (error == null) {
    return {};
  }
  const err = error as {
    name?: string;
    message?: string;
    code?: string;
    response?: { status?: number };
  } | null;
  const errName = (err?.name || 'Error').slice(0, 64);
  const httpStatus = err?.response?.status;
  const message = (err?.message || '').toLowerCase();

  if (typeof httpStatus === 'number') {
    return { errName, httpStatus, errorKind: 'http' };
  }
  if (err?.code === 'ECONNABORTED' || message.includes('timeout')) {
    return { errName, errorKind: 'timeout' };
  }
  if (message.includes('cors') || message.includes('cross-origin')) {
    return { errName, errorKind: 'cors' };
  }
  if (
    message.includes('network') ||
    message.includes('failed to fetch') ||
    err?.code === 'ERR_NETWORK'
  ) {
    return { errName, errorKind: 'network' };
  }
  return { errName, errorKind: 'thrown' };
};

export const readLoginFingerprint = (): Pick<
  AuthClientLogPayload,
  | 'hasPortalSessionsCookie'
  | 'lastAuthUser'
  | 'amplifyKeysPresent'
  | 'reloadCount'
> => {
  try {
    const cookie = typeof document === 'undefined' ? '' : document.cookie || '';
    const storageKeys =
      typeof window === 'undefined' ? [] : Object.keys(window.localStorage);
    // Cognito keys embed client id and username. Send only the last segment
    // (LastAuthUser, accessToken, …) so the fingerprint stays useful.
    const amplifyKeysPresent = [
      ...new Set(
        storageKeys
          .filter((key) => key.startsWith(AMPLIFY_LOCAL_STORAGE_PREFIX))
          .map((key) => key.slice(key.lastIndexOf('.') + 1)),
      ),
    ];
    const lastAuthUser = amplifyKeysPresent.includes('LastAuthUser');
    let reloadCount = 0;
    if (typeof window !== 'undefined') {
      const raw = window.sessionStorage.getItem(
        ASSEMBLY_LOGIN_RELOAD_COUNT_KEY,
      );
      const parsed = raw ? Number(raw) : 0;
      reloadCount = Number.isFinite(parsed) ? parsed : 0;
    }

    return {
      hasPortalSessionsCookie: cookie.includes('portal-sessions'),
      lastAuthUser,
      amplifyKeysPresent,
      reloadCount,
    };
  } catch {
    return {};
  }
};

export const bumpLoginReloadCount = () => {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const raw = window.sessionStorage.getItem(ASSEMBLY_LOGIN_RELOAD_COUNT_KEY);
    const next = (Number(raw) || 0) + 1;
    window.sessionStorage.setItem(
      ASSEMBLY_LOGIN_RELOAD_COUNT_KEY,
      String(next),
    );
  } catch {
    // Fingerprint storage must never block login.
  }
};

const capturePosthog = (payload: AuthClientLogPayload) => {
  try {
    posthog.capture(AUTH_CLIENT_FAILURE_EVENT, payload);
  } catch {
    // Ad blockers kill PostHog on the same machines with odd auth state.
  }
};

export const sendAuthClientLog = (input: {
  step: AuthClientLogStep;
  outcome: 'failure' | 'recovered';
  recoveryStage?: string;
  error?: unknown;
  email?: string;
  portalId?: string;
}) => {
  const loginAttemptId = getLoginAttemptId();
  const dedupeKey = `${loginAttemptId}:${input.step}`;
  if (sentBeacons.has(dedupeKey)) {
    return;
  }
  sentBeacons.add(dedupeKey);

  queueMicrotask(() => {
    const fingerprint = readLoginFingerprint();
    const classified = classifyAuthError(input.error);
    const payload: AuthClientLogPayload = {
      loginAttemptId,
      step: input.step,
      outcome: input.outcome,
      recoveryStage: input.recoveryStage,
      email: input.email?.toLowerCase(),
      portalId: input.portalId,
      ...classified,
      ...fingerprint,
    };

    try {
      void fetch(AUTH_CLIENT_LOG_PATH, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => undefined);
    } catch {
      // Beacon failures must not affect login.
    }

    capturePosthog(payload);
  });
};
